/*
 * vo-intl-final-sale.js
 * ---------------------------------------------------------------------------
 * International-orders-are-final-sale gating for the Prestige theme.
 *
 * Responsibilities (all client side):
 *   1. Detect whether the visitor is non-US, ONCE per session:
 *        - trust Shopify.country when it is already non-US (Markets context),
 *        - otherwise fall back to Cloudflare's edge trace (keyless) so that
 *          non-US visitors whose Shopify context still resolves to US are
 *          caught. Result cached in sessionStorage — no repeated requests,
 *          no flicker across cart-drawer re-renders.
 *   2. For non-US visitors, on every cart surface (cart page + cart drawer):
 *        - reveal the acknowledgment checkbox,
 *        - disable every checkout button until it is checked (click, keyboard,
 *          and form submit are all blocked),
 *        - persist the cart attribute "International final sale acknowledged".
 *      Gating is re-applied after the drawer's dynamic re-render.
 *   3. For non-US visitors, hide the product-page dynamic checkout button
 *      (Shop Pay / "Buy it now"), which bypasses the cart. Server-side markup
 *      already omits it when the market context is non-US; this is the safety
 *      net for the Cloudflare-detected case.
 *
 * Fail-open: if detection fails (network/CSP), the visitor is treated as US so
 * a script problem never blocks a checkout.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__voIntlFinalSaleInit) return;
  window.__voIntlFinalSaleInit = true;

  var ATTR_KEY = 'International final sale acknowledged';
  var CACHE_KEY = 'voIntlCountry';
  var HINT_TEXT = 'Tick the box above to continue to checkout.';

  var resolved = false;
  var isNonUS = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function shopifyCountry() {
    return (window.Shopify && window.Shopify.country ? String(window.Shopify.country) : '').toUpperCase();
  }

  // Resolve the visitor country once, then cache in sessionStorage.
  function resolveCountry(done) {
    if (resolved) { done(); return; }

    var finish = function (code) {
      code = (code || 'US').toUpperCase();
      try { sessionStorage.setItem(CACHE_KEY, code); } catch (e) {}
      isNonUS = code !== 'US';
      resolved = true;
      done();
    };

    var cached;
    try { cached = sessionStorage.getItem(CACHE_KEY); } catch (e) { cached = null; }
    if (cached) { isNonUS = cached.toUpperCase() !== 'US'; resolved = true; done(); return; }

    var ctx = shopifyCountry();
    if (ctx && ctx !== 'US') { finish(ctx); return; }

    // Shopify context is US (or unknown): confirm with the edge trace so a
    // non-US visitor without Markets geolocation is still caught.
    var timedOut = false;
    var timer = setTimeout(function () { timedOut = true; finish(ctx || 'US'); }, 2500);

    fetch('https://www.cloudflare.com/cdn-cgi/trace', { cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        if (timedOut) return;
        clearTimeout(timer);
        var m = text.match(/(?:^|\n)loc=([A-Z]{2})/);
        finish(m ? m[1] : (ctx || 'US'));
      })
      .catch(function () {
        if (timedOut) return;
        clearTimeout(timer);
        finish(ctx || 'US'); // fail-open
      });
  }

  function ackCheckboxes() {
    return document.querySelectorAll('[data-vo-intl-ack] input[type="checkbox"]');
  }

  function isAcknowledged() {
    var boxes = ackCheckboxes();
    if (!boxes.length) return false;
    return Array.prototype.some.call(boxes, function (b) { return b.checked; });
  }

  function setCheckoutDisabled(disabled) {
    var buttons = document.querySelectorAll('button[name="checkout"]');
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  }

  function showAckError(show) {
    var els = document.querySelectorAll('[data-vo-intl-error]');
    Array.prototype.forEach.call(els, function (el) { el.hidden = !show; });
  }

  // SITE-051: one line directly below the checkbox saying why Checkout is disabled.
  // Built here (non-US only) so US cart markup is unchanged; rebuilt after a drawer re-render.
  function ensureHint(wrap) {
    if (wrap.querySelector('[data-vo-intl-hint]')) return;
    var control = wrap.querySelector('.checkbox-control');
    if (!control) return;
    var p = document.createElement('p');
    p.className = 'vo-intl-ack__hint text-sm';
    p.setAttribute('data-vo-intl-hint', '');
    p.textContent = HINT_TEXT;
    control.insertAdjacentElement('afterend', p);
  }

  function showHint(show) {
    var els = document.querySelectorAll('[data-vo-intl-hint]');
    Array.prototype.forEach.call(els, function (el) { el.hidden = !show; });
  }

  // Reveal the checkbox + gate the checkout button(s) on every cart surface.
  function applyCartGate() {
    if (!isNonUS) return;
    var wraps = document.querySelectorAll('[data-vo-intl-ack]');
    if (!wraps.length) return; // not on a cart surface
    Array.prototype.forEach.call(wraps, function (w) { w.hidden = false; ensureHint(w); });

    var ok = isAcknowledged();
    setCheckoutDisabled(!ok);
    showHint(!ok);
    if (ok) showAckError(false);
  }

  // Hide the product-page dynamic checkout button (bypasses the cart).
  function hideDynamicCheckout() {
    if (!isNonUS) return;
    var els = document.querySelectorAll('.shopify-payment-button');
    Array.prototype.forEach.call(els, function (el) { el.style.display = 'none'; });
  }

  function writeAttribute(yes) {
    var body = { attributes: {} };
    body.attributes[ATTR_KEY] = yes ? 'Yes' : '';
    fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(function () {});
  }

  function apply() {
    applyCartGate();
    hideDynamicCheckout();
  }

  // Checkbox toggled: sync every ack checkbox on the page, persist, re-gate.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.closest('[data-vo-intl-ack]') || t.type !== 'checkbox') return;
    var yes = t.checked;
    Array.prototype.forEach.call(ackCheckboxes(), function (b) { b.checked = yes; });
    writeAttribute(yes);
    applyCartGate();
  });

  // Block checkout submission (Enter key / programmatic) when unacknowledged.
  document.addEventListener('submit', function (e) {
    if (!isNonUS) return;
    var form = e.target;
    if (!form || !form.matches || !(form.matches('.cart-page') || form.matches('.cart-drawer__footer'))) return;
    var submitter = e.submitter;
    if (submitter && submitter.name && submitter.name !== 'checkout') return; // allow non-checkout submits
    if (!isAcknowledged()) {
      e.preventDefault();
      e.stopPropagation();
      applyCartGate();
      showAckError(true);
      var box = ackCheckboxes()[0];
      if (box && box.focus) box.focus();
    }
  }, true);

  // Re-apply after the theme mutates the cart (drawer replaces its DOM).
  ['cart:change', 'cart-drawer:refreshed', 'cart:refresh'].forEach(function (ev) {
    document.addEventListener(ev, function () {
      if (resolved) { apply(); } else { resolveCountry(apply); }
    });
  });

  function init() { resolveCountry(apply); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
